import type {ReactNode} from "react";

import {cn} from "@project/design-system-web";

interface TableToolbarProps {
  children: ReactNode;
}

interface TableToolbarActionsProps {
  children: ReactNode;
  className?: string;
}

/**
 * Actions slot — right-aligned action area within the toolbar.
 *
 * Houses buttons, dropdowns, and other controls that act on the table as a whole
 * (e.g. Create, Filter, Export). Rendered to the right of the search field on
 * wider viewports.
 */
function TableToolbarActions({children, className}: TableToolbarActionsProps) {
  return <div className={cn("flex gap-2", className)}>{children}</div>;
}

/**
 * TableToolbar — responsive layout wrapper for table toolbars.
 *
 * Compound component with a single slot: `TableToolbar.Actions`.
 *
 * On mobile the toolbar stacks vertically; on `md+` viewports it switches to a
 * horizontal row with the search field aligned left and actions aligned right.
 *
 * @example
 * ```tsx
 * <TableToolbar>
 *   <TableSearchField value={q} onChange={setQ} placeholder="Search users…" />
 *   <TableToolbar.Actions>
 *     <ButtonGroup>
 *       <Dropdown>...</Dropdown>
 *       <Button variant="primary">Create</Button>
 *     </ButtonGroup>
 *   </TableToolbar.Actions>
 * </TableToolbar>
 * ```
 */
function TableToolbarRoot({children}: TableToolbarProps) {
  return (
    <div className="flex flex-col gap-3 @lg:flex-row @lg:items-end @lg:justify-between">
      {children}
    </div>
  );
}

TableToolbarRoot.Actions = TableToolbarActions;

export const TableToolbar = TableToolbarRoot;
